import { useState } from 'react';
import { useNavigate } from 'react-router';

// material-ui
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

// project imports
import Logo from 'components/logo';
import Avatar from 'components/@extended/Avatar';
import useAuth from 'hooks/useAuth';
import { useAdminContext } from 'contexts/AdminContext';

// assets
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import avatar1 from 'assets/images/users/avatar-1.png';
import { useTranslation } from 'hooks/useTranslation';

// ==============================|| ADMIN HEADER ||============================== //

export default function AdminHeader() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { profile } = useAdminContext();
  const t = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/auth/login/email', {
        state: {
          from: ''
        }
      });
    } catch (err) {
      console.error(err);
    }
    handleClose();
  };

  const handleSettingsClick = () => {
    navigate('/admin/settings');
  };

  const handleCommentsClick = () => {
    navigate('/admin/comments');
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        bgcolor: 'background.paper',
        color: 'text.primary',
        boxShadow: 'none',
        borderBottom: '1px solid',
        borderBottomColor: 'divider',
        zIndex: 1200
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', px: 3 }}>
        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Logo isIcon={false} sx={{ width: 'auto', height: 32 }} logoSize={32} fontSize={16} to="/admin" />
        </Box>

        {/* Right side - Comments, Settings and User */}
        <Stack direction="row" spacing={1} alignItems="center">
          {/* Comments Icon */}
          <IconButton size="large" color="inherit" onClick={handleCommentsClick}>
            <MessageOutlined />
          </IconButton>

          {/* Settings Icon */}
          <IconButton size="large" color="inherit" onClick={handleSettingsClick}>
            <SettingOutlined />
          </IconButton>

          {/* User Profile */}
          <ButtonBase
            onClick={handleClick}
            sx={{
              p: 0.5,
              borderRadius: 1,
              '&:hover': { bgcolor: 'action.hover' }
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar alt="profile user" src={profile?.avatar || avatar1} size="sm" />
              <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
                {profile?.name || user?.name || 'Admin'}
              </Typography>
            </Stack>
          </ButtonBase>

          {/* User Menu */}
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
          >
            <MenuItem onClick={handleLogout}>
              <LogoutOutlined style={{ marginRight: 8 }} />
              {t('Logout')}
            </MenuItem>
          </Menu>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}
